import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { StringFormatter } from '@repo/toolkit/string'
import { useNavigator } from '@repo/toolkit/use-navigator'
import ActionButton from '@repo/ui/action-button'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import DetailedTable, { DetailedTableColumn } from '@repo/ui/detailed-table'
import { ArrowLeftRight, DeleteIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Fragment } from 'react/jsx-runtime'

import { assignTransferToTripFolder } from '../../../../api/trip-folder/assign-transfer-to-trip-folder'
import { getTripFolderSecondaryBookings } from '../../../../api/trip-folder/get-trip-folder-secondary-bookings'
import { unassignTransferFromTripFolder } from '../../../../api/trip-folder/unassign-transfer-from-trip-folder'
import { updateTransferPlace } from '../../../../api/trip-folder/update-transfer-trip-folder'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../../lib/hooks/use-translate'
import { staffPositionEnumMapping } from '../../../../lib/types/enums/staff/staff-position.enum'
import { TransferPlace } from '../../../../lib/types/models/transfer/transfer-place'
import { TripBookingTypes } from '../../../../lib/types/models/trip-booking/trip-booking'
import { TripBookingDispatchPlaceIndividualTypeEnum } from '../../../../lib/types/models/trip-booking/trip-date'
import { TripBookingDispatchPlaceProperties } from '../../../../lib/types/models/trip-booking-dispatch-places/trip-booking-dispatch-place.properties'
import { TripFolderSecondaryTripBooking } from '../../../../lib/types/models/trip-folder/trip-folder'
import { AssignTransferToTripFolderInput } from '../../../../lib/types/models/trip-folder/trip-folder-input'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { getTranslatedName } from '../../../../lib/utils/functions/translation.functions'
import { queryClient } from '../../../../main'
import TripBookingBadge from './trip-booking-badge'
import TripFolderAssignTransferForm from './trip-folder-assign-transfer-form'

interface Props {
  folderId: number
  hiddenColumns?: (keyof TransformTripFolderSecondaryTripBooking)[]
}

export type TransformTripFolderSecondaryTripBooking = Omit<
  TripFolderSecondaryTripBooking,
  'arrivalPlace'
> & {
  arrivalPlaceLabel: string
  arrivalPlaceId: number
}

const transformTripFolderSecondaryTripBooking = (
  tripFolderSecondaryTripBooking: TripFolderSecondaryTripBooking
): TransformTripFolderSecondaryTripBooking => {
  return {
    ...tripFolderSecondaryTripBooking,
    arrivalPlaceLabel: tripFolderSecondaryTripBooking.arrivalPlace.label,
    arrivalPlaceId: tripFolderSecondaryTripBooking.arrivalPlace.id
  }
}

const TripFolderSecondaryTripSection = ({ folderId, hiddenColumns }: Props) => {
  const { navigate } = useNavigator()
  const { t } = useTranslate()

  const { data: secondaryTripBookings, isPending } = useQuery({
    queryKey: ['trip-folder-secondary-trip-bookings', folderId],
    queryFn: () => getTripFolderSecondaryBookings(folderId),
    select: (data) => data.map(transformTripFolderSecondaryTripBooking)
  })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'assign' | 'view',
    TripBookingTypes.Light & { tripFolderTransferTripBookingId: number }
  >(['assign', 'view'])

  const {
    modalState: modalStateTransfer,
    selectedData: selectedDataTransfer,
    toggleModal: toggleModalTransfer,
    closeModel: closeModelTransfer
  } = useModalManager<'view', TransferPlace>(['view'])

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: ['trip-folder-secondary-trip-bookings', folderId]
    })
    queryClient.invalidateQueries({
      queryKey: ['trip-folder-transfers', folderId]
    })
  }
  const { mutateAsync } = useMutation({
    mutationFn: assignTransferToTripFolder,
    onSuccess: () => {
      closeModel('assign')
      invalidateQueries()
    },
    onError: showAxiosError
  })

  const { mutateAsync: unassignTransfer, isPending: isUnassigningTransfer } = useMutation({
    mutationFn: unassignTransferFromTripFolder,
    onSuccess: () => {
      closeModelTransfer('view')
      invalidateQueries()
    },
    onError: showAxiosError
  })

  const { mutateAsync: updateTransfer } = useMutation({
    mutationFn: updateTransferPlace,
    onSuccess: () => {
      closeModelTransfer('view')
      invalidateQueries()
    },
    onError: showAxiosError
  })

  const assignTransfer = async (data: AssignTransferToTripFolderInput) => {
    if (!selectedData) return

    await mutateAsync({
      ...data,
      id: folderId,
      tripFolderTransferTripBookingId: selectedData.tripFolderTransferTripBookingId
    })
  }

  const columns: DetailedTableColumn<TransformTripFolderSecondaryTripBooking>[] = [
    {
      header: "Lieu d'arrivée",
      key: 'arrivalPlaceLabel',
      render: (row) => (
        <Typography.Link
          variant="secondary-body"
          onClick={() =>
            navigate(Paths.TRIP_ADDRESS_UPDATE_PAGE.replace(':id', row.arrivalPlaceId.toString()))
          }
        >
          {row.arrivalPlaceLabel}
        </Typography.Link>
      )
    },
    {
      header: "Heure d'arrivée",
      key: 'arrivalTime',
      render: (row) => (
        <Badge
          text={valueRender(row.arrivalTime, (time) => DateFormatter.getFormattedTime(time))}
        />
      )
    },
    {
      header: 'Voyage secondaire',
      key: 'tripBooking',
      render: (row) => (
        <TripBookingBadge
          tripBooking={row.tripBooking}
          displayFormat={'default'}
          infoType={'reserved'}
        />
      )
    },
    {
      header: 'Encadrants',
      key: 'dispatchPlaces',
      render: (row) => (
        <div className="flex flex-col gap-1">
          {row.dispatchPlaces?.length === 0 && '-'}
          {row.dispatchPlaces?.map((dispatchPlace) => {
            if (
              dispatchPlace.individualType ===
              TripBookingDispatchPlaceIndividualTypeEnum.ENROLLMENT
            )
              return '-'

            const staff = TripBookingDispatchPlaceProperties.getStaffInformation(dispatchPlace)
            return (
              <Badge
                key={dispatchPlace.id}
                text={`${staff.lastName.toUpperCase()} ${StringFormatter.capitalize(staff.firstName)} - ${t(staffPositionEnumMapping[staff.position])} ${staff.phoneNumber ? `(${staff.phoneNumber})` : ''}`}
              />
            )
          })}
        </div>
      )
    },
    {
      header: 'Transferts',
      key: 'transferPlaces',
      render: (row) => (
        <div className={'flex gap-2'}>
          <div className={'flex flex-col gap-1 py-1.5'}>
            {row.transferPlaces?.map((transferPlace) => (
              <Badge
                key={transferPlace.id}
                text={`${transferPlace.transfer.id} - ${getTranslatedName(transferPlace.transfer.transportMeans)} - (${DateFormatter.getFormattedTime(transferPlace.transfer.departureTime)} -> ${DateFormatter.getFormattedTime(transferPlace.transfer.arrivalTime)}) - # Jeunes: ${transferPlace.transfer.numberOfStudents} - # Encadrants: ${transferPlace.transfer.numberOfStaff}`}
                color={tailwindColors.white.hover}
                borderColor={tailwindColors.grey.light.DEFAULT}
                className={'hover:!bg-primary-light hover:!border-primary-light-active'}
                size={'shrink'}
                onClick={() => {
                  if (transferPlace) toggleModalTransfer('view', transferPlace)
                }}
              />
            ))}
          </div>
          <ActionButton
            variant="primary"
            icon={PlusIcon}
            onClick={() => {
              toggleModal('assign', {
                ...row.tripBooking,
                tripFolderTransferTripBookingId: row.id
              })
            }}
          />
        </div>
      )
    }
  ]

  if (isPending) return <Skeleton className={'h-[300px] w-full'} />

  if (!isPending && (!secondaryTripBookings || secondaryTripBookings?.length === 0)) {
    return (
      <div className="flex justify-center items-center p-8 bg-gray-50 rounded-3xl border border-primary-light">
        <Typography variant={'secondary-body'}>Aucun voyage secondaire disponible</Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <DetailedTable<TransformTripFolderSecondaryTripBooking>
        groupBy={['arrivalPlaceLabel']}
        data={secondaryTripBookings ?? []}
        columns={columns.filter((column) => !hiddenColumns?.includes(column.key))}
      />

      <Modal
        isOpen={modalState.assign}
        title={'Associer un transfert'}
        icon={ArrowLeftRight}
        size="small"
        hideActions
        onCancel={() => toggleModal('assign')}
      >
        {selectedData && (
          <TripFolderAssignTransferForm
            tripBooking={selectedData}
            tripFolderId={folderId}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('assign'),
              onConfirm: assignTransfer
            })}
          />
        )}
      </Modal>

      <Modal
        isOpen={modalStateTransfer.view}
        title={`Modifier un transfert`}
        icon={ArrowLeftRight}
        size="medium"
        hideActions
        onCancel={() => closeModelTransfer('view')}
      >
        {selectedDataTransfer && (
          <TripFolderAssignTransferForm
            defaultValues={{
              transferId: selectedDataTransfer.transfer.id,
              numberOfPlacesDispatched: selectedDataTransfer.numberOfPlacesDispatched,
              missionToStaffIds: selectedDataTransfer.missionToStaffs?.map(({ id }) => id),
              missionToExternalStaffIds: selectedDataTransfer.missionToExternalStaffs?.map(
                ({ id }) => id
              )
            }}
            tripBooking={selectedDataTransfer.tripFolderTransferTripBooking.tripBooking}
            tripFolderId={folderId}
            preComponent={
              <Button
                type="button"
                variant="destructive"
                icon={DeleteIcon}
                loading={isUnassigningTransfer}
                onClick={() =>
                  unassignTransfer({
                    transferId: selectedDataTransfer.id,
                    tripFolderId: folderId
                  })
                }
                label="Supprimer"
              />
            }
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModelTransfer('view'),
              onConfirm: async (data) => {
                await updateTransfer({
                  ...data,
                  transferId: selectedDataTransfer.id,
                  tripFolderId: folderId
                })
              }
            })}
          />
        )}
      </Modal>
    </Fragment>
  )
}

export default TripFolderSecondaryTripSection
